import { useEffect } from 'react';
import { apiGet } from '../api/client';

interface PendingResponse {
  count: number;
  ids: string[];
}

/**
 * Stage 25: generalised pending-poller for any async media gallery
 * (images / music / speech / video). While the gallery is mounted,
 * polls `${endpoint}` every 15 s. When the pending-ids set changes
 * (a job finished or a new one started), calls `refresh()` to
 * re-fetch the gallery list so "Generating…" tiles transition to
 * playable cards.
 *
 * 15 s cadence matches the cron's 60 s sweep cycle: worst-case
 * ready-to-visible latency is ~15 s for synchronous-provider tools
 * (image/music/speech) and ~15 s + 60 s = 75 s for the provider-async
 * video tool. Acceptable for multi-minute generations.
 */
export function usePendingMediaPoller(
  endpoint: string,
  refresh: () => Promise<void>,
): void {
  useEffect(() => {
    let cancelled = false;
    let lastHash = '';

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const res = await apiGet<PendingResponse>(endpoint);
        const hash = `${res.count}:${res.ids.slice().sort().join(',')}`;
        if (hash !== lastHash) {
          lastHash = hash;
          await refresh();
        }
      } catch {
        // Swallow — next tick tries again.
      }
    };

    void tick();
    const handle = window.setInterval(() => {
      void tick();
    }, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [endpoint, refresh]);
}
